/**
 * Issue #263 — 合规通话目标数（KPI考核）纯逻辑 + 文案常量。
 *
 * admin vitest 无路由 / 组件渲染夹具（UI 由 Playwright e2e 覆盖），因此把「能否保存 /
 * 区间校验 / 钳制 / 文案」抽成纯函数：CompliantCallTargetSettingsCard 仅做薄包装并复用，
 * 关键决策（尤其「只读角色绝不触发保存」「0/1001/非整数判否」）由单测锁定。区间 / 默认值
 * 以后端下发为准（1-1000，默认 5）。
 */
export const COMPLIANT_CALL_TARGET_LABEL = '合规通话目标';

/** 兜底默认（仅在后端尚未下发时占位；正式取值始终以接口返回为准）。 */
export const COMPLIANT_CALL_TARGET_DEFAULT = 5;
export const COMPLIANT_CALL_TARGET_MIN = 1;
export const COMPLIANT_CALL_TARGET_MAX = 1000;

export interface CompliantCallTargetRange {
  min: number;
  max: number;
  defaultTarget: number;
}

/** 接口缺失时使用的兜底区间（与后端常量一致）。 */
export const DEFAULT_COMPLIANT_CALL_TARGET_RANGE: CompliantCallTargetRange = {
  min: COMPLIANT_CALL_TARGET_MIN,
  max: COMPLIANT_CALL_TARGET_MAX,
  defaultTarget: COMPLIANT_CALL_TARGET_DEFAULT,
};

/** 草稿字符串 → 是否为合法整数且落在区间内（非数字 / 小数 / 越界 / 0 / 1001 一律判否）。 */
export function isCompliantCallTargetInRange(
  raw: string,
  range: CompliantCallTargetRange,
): boolean {
  const trimmed = raw.trim();
  if (trimmed === '') return false;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || !Number.isInteger(n)) return false;
  return n >= range.min && n <= range.max;
}

/** 把任意输入钳制到合法区间整数（与后端 clampCompliantCallTarget 同语义，前端展示兜底）。 */
export function clampCompliantCallTarget(
  value: number,
  range: CompliantCallTargetRange,
): number {
  if (!Number.isFinite(value)) return range.defaultTarget;
  const intVal = Math.round(value);
  if (intVal < range.min) return range.min;
  if (intVal > range.max) return range.max;
  return intVal;
}

/** 区间提示文案（如「1-1000，默认 5」）。 */
export function compliantCallTargetRangeHint(range: CompliantCallTargetRange): string {
  return `${range.min}-${range.max}，默认 ${range.defaultTarget}`;
}

/**
 * 能否保存：必须可编辑（ADMIN/SUPER_ADMIN）、未在保存中、草稿在合法区间。
 * `canEdit=false`（只读 / 非编辑角色）永远返回 false —— 任何只读路径都不会触发真实 PATCH。
 */
export function canSaveCompliantCallTarget(args: {
  canEdit: boolean;
  pending: boolean;
  raw: string;
  range: CompliantCallTargetRange;
}): boolean {
  if (!args.canEdit) return false;
  if (args.pending) return false;
  return isCompliantCallTargetInRange(args.raw, args.range);
}
